import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { Button } from "../../../components/Button";
import Modal from "../../../components/Modal/Modal";
import Titulo from "../../../components/Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { AssinaturaApi } from "../../../hooks/assinaturaApi";
import { CicloAberto, ResultadoGerarPix } from "../../../types/Assinatura";
import { ViewPagarFatura, ViewModalPagamento } from "../styles";
import { ModalAtualizarCartao } from "./ModalAtualizarCartao";

interface PagarFaturaProps {
  cicloAberto: CicloAberto;
  onSucesso: () => void;
  maxParcelas?: number;
}

const POLLING_INTERVAL_MS = 10_000;
const POLLING_DURACAO_MS = 120_000; // 2 min

/**
 * Seção "Pagar fatura aberta" da rota /assinatura. Duas ações:
 * atualizar cartão (modal com form + cobrança síncrona) e pagar com PIX
 * (modal com QR + polling automático 2 min consultando se pagou). Fora
 * do modal, botão "Já paguei, atualizar" aparece quando há PIX gerado.
 */
export const PagarFatura: React.FC<PagarFaturaProps> = ({ cicloAberto, onSucesso, maxParcelas }) => {
  const { theme } = useTheme();

  const [modalCartao, setModalCartao] = useState(false);
  const [modalPix, setModalPix] = useState(false);

  // PIX
  const [pixResultado, setPixResultado] = useState<ResultadoGerarPix | null>(null);
  const [gerandoPix, setGerandoPix] = useState(false);

  // Polling PIX
  const [aguardandoPix, setAguardandoPix] = useState(false);
  const [pollingEsgotou, setPollingEsgotou] = useState(false);
  const [verificandoManual, setVerificandoManual] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const formatarValor = (v: number) =>
    v.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
  const formatarData = (d: string) =>
    moment(d, "YYYY-MM-DD").format("DD/MM/YYYY");

  const pararPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setAguardandoPix(false);
  };

  const confirmarPagamento = async () => {
    await Swal.fire({
      icon: "success",
      title: "Pagamento confirmado!",
      text: "Sua assinatura está em dia novamente.",
    });
    window.location.reload();
  };

  // Polling enquanto ha QR exibido — no modal (cartao→PIX on-demand) ou
  // inline (PIX recorrente).
  const pollingAtivo = !!pixResultado && (modalPix || (cicloAberto.forma_pagamento === "pix" && !!cicloAberto.pagarme_charge_id));
  useEffect(() => {
    if (!pollingAtivo) {
      pararPolling();
      return;
    }

    setAguardandoPix(true);
    setPollingEsgotou(false);

    intervalRef.current = setInterval(async () => {
      try {
        const r = await AssinaturaApi().verificarPagamentoPix();
        if (r.pago) {
          pararPolling();
          await confirmarPagamento();
        }
      } catch (e) {
        console.error("Erro no polling PIX:", e);
      }
    }, POLLING_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      pararPolling();
      setPollingEsgotou(true);
    }, POLLING_DURACAO_MS);

    return pararPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingAtivo]);

  const handleGerarPix = async (abrirModal = true) => {
    setGerandoPix(true);
    try {
      const result = await AssinaturaApi().gerarPixFatura();

      // Edge case: charge anterior já foi paga — admin reconciliou na hora
      if (result.ja_pago) {
        await confirmarPagamento();
        return;
      }

      if (result.ok) {
        setPixResultado(result);
        if (abrirModal) setModalPix(true);
      } else {
        Swal.fire({ icon: "error", title: "Não foi possível gerar PIX" });
      }
    } catch (err: any) {
      console.error("Erro ao gerar PIX:", err);
      Swal.fire({
        icon: "error",
        title: "Erro ao gerar PIX",
        text:
          err?.response?.data?.erro || "Não foi possível gerar o PIX. Tente novamente.",
      });
    } finally {
      setGerandoPix(false);
    }
  };

  const handleCopiarPix = () => {
    if (pixResultado?.qr_code) {
      navigator.clipboard.writeText(pixResultado.qr_code);
      Swal.fire({
        icon: "success",
        title: "Código PIX copiado!",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleJaPaguei = async () => {
    setVerificandoManual(true);
    try {
      const r = await AssinaturaApi().verificarPagamentoPix();
      if (r.pago) {
        pararPolling();
        await confirmarPagamento();
      } else {
        Swal.fire({
          icon: "info",
          title: "Pagamento ainda não confirmado",
          text: "Estamos aguardando a confirmação do banco. Tente novamente em alguns instantes.",
        });
      }
    } catch (e) {
      console.error("Erro ao verificar pagamento:", e);
      Swal.fire({ icon: "error", title: "Erro ao verificar pagamento" });
    } finally {
      setVerificandoManual(false);
    }
  };

  // PIX recorrente: ciclo ja foi criado com forma=pix pelo Job A (com charge_id).
  // UX: carrega o QR automaticamente ao montar e exibe INLINE na tela (sem modal).
  // Cliente nao precisa clicar "Pagar com PIX" — ja ve o QR pronto.
  const ehPixRecorrente =
    cicloAberto.forma_pagamento === "pix" && !!cicloAberto.pagarme_charge_id;

  // Botão "Já paguei" fora do modal — só se já houve PIX gerado pra esse ciclo
  const temPixGerado =
    cicloAberto.forma_pagamento === "pix" && !!cicloAberto.pagarme_charge_id;

  // Auto-carrega o QR pra PIX recorrente. Reusa charge existente (sem custo extra).
  // Nao abre modal — exibimos o QR inline na tela.
  const autoLoadTentadoRef = useRef(false);
  useEffect(() => {
    if (ehPixRecorrente && !pixResultado && !autoLoadTentadoRef.current && !gerandoPix) {
      autoLoadTentadoRef.current = true;
      handleGerarPix(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehPixRecorrente]);


  // Retenção fiscal: valor a pagar = líquido; bruto = líquido + retido.
  // Figma mostra o líquido com "*" e a nota "* R$ bruto do plano - R$ retido…".
  const valorRetido = Number(cicloAberto.valor_total_retido ?? 0);
  const temRetencao = valorRetido > 0;
  const valorBruto = Number(cicloAberto.valor_cobrado) + valorRetido;

  return (
    <ViewPagarFatura theme={theme}>
      <div className="header-fatura">
        <div className="secao-titulo">Fatura aberta</div>
        {temPixGerado && !modalPix && (
          <button
            type="button"
            className="link-ja-paguei"
            onClick={handleJaPaguei}
            // Durante o polling automático (2 min) o botão vira spinner e fica
            // travado — não faz sentido verificar manual enquanto já checamos.
            disabled={aguardandoPix || verificandoManual}
          >
            {aguardandoPix || verificandoManual ? (
              <>
                <span className="spinner-inline" />
                Verificando pagamento...
              </>
            ) : (
              "↻ Já paguei, atualizar"
            )}
          </button>
        )}
      </div>

      <div className="bloco-forms">
        <div className="linha-dupla">
          <div className="campo">
            <span className="campo-label">Valor</span>
            <div className="campo-valor">
              R$ {formatarValor(cicloAberto.valor_cobrado)}
              {temRetencao && "*"}
            </div>
          </div>
          <div className="campo">
            <span className="campo-label">Vencimento</span>
            <div className="campo-valor">{formatarData(cicloAberto.data_vencimento)}</div>
          </div>
        </div>
        {temRetencao && (
          <p className="nota-retencao">
            *R$ {formatarValor(valorBruto)} do plano - R$ {formatarValor(valorRetido)} da
            retenção fiscal
          </p>
        )}
      </div>

      {/* PIX recorrente: QR à esquerda, instruções + copiar + expiração à direita */}
      {ehPixRecorrente && pixResultado && (
        <div className="pix-inline">
          <div className="pix-conteudo">
            {pixResultado.qr_code_url && (
              <div className="qr-wrapper">
                <img src={pixResultado.qr_code_url} alt="QR Code PIX" className="qr-imagem" />
              </div>
            )}
            <div className="pix-info">
              <p className="pix-instrucao">
                Escaneie o QR Code ou copie o código Pix e cole o código no aplicativo do seu
                banco para efetuar o pagamento.
              </p>
              {pixResultado.qr_code && (
                <button type="button" className="btn-copiar-pix" onClick={handleCopiarPix}>
                  Copiar código Pix Copia e Cola
                </button>
              )}
              <p className="pix-aviso-texto">
                Após o pagamento, a confirmação pode levar alguns segundos. Esta tela atualiza
                automaticamente quando o pagamento for reconhecido.
                {pixResultado.expires_at && (
                  <>
                    <br />
                    <br />
                    <strong>
                      QR Code expira em:{" "}
                      {moment(pixResultado.expires_at).format("DD/MM/YYYY HH:mm")}
                    </strong>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {ehPixRecorrente && gerandoPix && !pixResultado && (
        <div className="aguardando-pagamento">
          <div className="spinner" />
          <span>Carregando QR Code PIX...</span>
        </div>
      )}

      {/* Ações no rodapé (Figma: "Pagar com cartão" à direita, após o QR). */}
      <div className="acoes-fatura">
        <button type="button" onClick={() => setModalCartao(true)}>
          {ehPixRecorrente ? "Pagar com cartão" : "Atualizar cartão"}
        </button>
        {!ehPixRecorrente && (
          <button type="button" onClick={() => handleGerarPix()} disabled={gerandoPix}>
            {gerandoPix ? "Gerando PIX..." : "Pagar com PIX"}
          </button>
        )}
      </div>

      {/* Modal: atualizar cartão (form compartilhado) */}
      <ModalAtualizarCartao
        isOpen={modalCartao}
        setOpen={setModalCartao}
        onSucesso={onSucesso}
        valorCobranca={cicloAberto.valor_cobrado}
        maxParcelas={maxParcelas}
      />

      {/* Modal: PIX */}
      <Modal
        isOpen={modalPix}
        setModal={(v: boolean) => {
          setModalPix(v);
          if (!v) pararPolling();
        }}
        width={"65rem"}
        align="center"
        justify="center"
        animation="opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)"
        margin="0rem 2rem"
        padding={"3rem"}
      >
        <Titulo
          justifyContent="center"
          flexDirection="column-reverse"
          titulo="Pague com PIX"
          subtitulo={`Valor: R$ ${formatarValor(cicloAberto.valor_cobrado)}`}
        />
        <ViewModalPagamento theme={theme}>
          {pixResultado?.qr_code_url && (
            <div className="qr-wrapper">
              <img
                src={pixResultado.qr_code_url}
                alt="QR Code PIX"
                className="qr-imagem"
              />
            </div>
          )}

          {pixResultado?.qr_code && (
            <>
              <div className="pix-codigo-label fonteTexto">Ou copie o código PIX:</div>
              <div className="pix-codigo">{pixResultado.qr_code}</div>
              <div className="acoes-modal">
                <Button
                  title="Copiar código"
                  className="fonteTexto"
                  onClick={handleCopiarPix}
                  width="auto"
                  padding="0 2rem"
                />
              </div>
            </>
          )}

          {aguardandoPix && (
            <div className="aguardando-pagamento">
              <div className="spinner" />
              <span className="fonteTexto">Aguardando confirmação do pagamento...</span>
            </div>
          )}

          {pollingEsgotou && (
            <div className="ja-paguei-wrapper">
              <span className="fonteTexto pix-aviso-texto">
                Já efetuou o pagamento? Clique abaixo para verificar.
              </span>
              <Button
                title={verificandoManual ? "Verificando..." : "Já paguei, atualizar"}
                className="fonteTexto"
                onClick={handleJaPaguei}
                width="auto"
                padding="0 2rem"
                loading={verificandoManual}
              />
            </div>
          )}

          <div className="pix-aviso fonteTexto">
            Após o pagamento, a confirmação pode levar alguns segundos. Esta
            tela atualiza automaticamente quando o pagamento for reconhecido.
            {pixResultado?.expires_at && (
              <div>
                <strong>QR Code expira em:</strong>{" "}
                {moment(pixResultado.expires_at).format("DD/MM/YYYY HH:mm")}
              </div>
            )}
          </div>

          <div className="acoes-modal">
            <Button
              title="Fechar"
              className="fonteTexto botao-secundario"
              onClick={() => {
                setModalPix(false);
                pararPolling();
              }}
              width="auto"
              padding="0 2rem"
            />
          </div>
        </ViewModalPagamento>
      </Modal>
    </ViewPagarFatura>
  );
};

export default PagarFatura;
